<template>
  <article>
    <AppBanner title="FAQs" />

    <section
      v-for="(faq, faqIndex) in faqs"
      :key="faqIndex"
      class="odd:bg-secondary-subtle"
    >
      <AppAccordion
        :title="faq.topic"
        class="container-padding"
      >
        <AppAccordionItem
          v-for="(question, index) in faq.questions"
          :key="index"
          :title="question.question"
          :index="index"
        >
          {{ question.answer }}
        </AppAccordionItem>
      </AppAccordion>
    </section>
  </article>
</template>

<script setup>

import {
  AppAccordion,
  AppAccordionItem,
  AppBanner,
} from '@/components/app';

const faqs = [
  {
    topic: 'Finding Your Photos',
    questions: [
      {
        question: 'What do I do if my photos aren’t showing when using my race number?',
        answer: 'Sometimes bib numbers become partially obscured or unreadable to our recognition technology. In these cases, check the event folders for the section labelled "Unidentified Photos" or "Race Number Obscured" to view images manually and add them to your cart.',
      },
      {
        question: 'What if my event doesn\'t appear in your listings?',
        answer: 'If you can\'t see your event, it may still be uploading, or SportPix may not have photographed the event. Feel free to contact your event organiser or contact us to enquire if SportPix was the official photographer.',
      },
    ],
  },
  {
    topic: 'Placing and Customising Your Order',
    questions: [
      {
        question: 'How do I order my race photos?',
        answer: 'Once you\'ve found your event and photos, select the images you wish to buy and simply add them to your cart. When you\'re ready, proceed to checkout, select your preferred format (digital or print), and securely complete your payment.',
      },
      {
        question: 'Can I order images from multiple events at once?',
        answer: 'Yes! Your shopping cart keeps your selected photos from different events. Feel free to browse, add images from as many events as you like, then proceed to checkout.',
      },
      {
        question: 'Can I purchase all photos associated with my bib number at once?',
        answer: 'Yes. For many events, you have the option to purchase a complete "bulk pack" containing all matched photos of your specific bib number.',
      },
      {
        question: 'Can I choose different sizes and formats for my photos?',
        answer: 'Yes, you can select from Standard Size (10x15 cm), Jumbo (15x20 cm), A4 print or even larger A3 canvas options. Digital downloads come in Standard resolution (ideal for sharing) or Original High-Resolution quality (ideal for professional printing).',
      },
      {
        question: 'Can I select my photo style or have event info shown on the photo?',
        answer: 'Yes, premium images allow you to choose between plain photos or a customised overlay featuring event details and branding.',
      },
    ],

  },
  {
    topic: 'Digital and Print Delivery',
    questions: [
      {
        question: 'Will I receive digital photos immediately after payment?',
        answer: 'Yes, standard digital images are available instantly. The download link is emailed within minutes of your successful payment.',
      },
      {
        question: 'When do I get my high-res digital versions?',
        answer: 'Original high-resolution photographs are uploaded approximately 2–3 days after each event. However, you can still purchase the high-res images in the meantime, and you will be able to download the standard-resolution images while the high-res images are being processed.',
      },
      {
        question: 'Will I receive a digital version if I order a printed photo?',
        answer: 'Yes, all printed products include a digital download version at no additional cost.',
      },
      {
        question: 'If I buy a digital version, will you also send me a physical print?',
        answer: 'No, digital-only purchases do not include physical prints. You can print this yourself at your convenience.',
      },
      {
        question: 'What print sizes do you offer, and what quality can I expect?',
        answer: 'Current print sizes are Standard (10x15 cm), Jumbo (15x20 cm), A4 prints, and an A3 canvas option. We use professional-grade gloss photographic paper and premium printing technology with no fading or smudging.',
      },
    ],
  },
  {
    topic: 'Payments and Checkout',
    questions: [
      {
        question: 'What payment methods do you accept?',
        answer: 'We accept secure online payments via PayFast. You can pay using credit card, debit card, Zapper, or SnapScan.',
      },
      {
        question: 'Is my payment secure?',
        answer: 'Absolutely. All payments are securely processed through the trusted and secure PayFast gateway. We don\'t store your banking or credit card details.',
      },
      {
        question: 'When will my printed photos arrive after ordering?',
        answer: 'Typically, prints arrive within 2–7 days depending on your location. You\'ll receive tracking info once your order ships.',
      },
      {
        question: 'What happens if I don\'t receive my order?',
        answer: 'Simply contact us via our Contact page with your order information, and we\'ll immediately investigate and resolve your issue.',
      },
    ],
  },
  {
    topic: 'Downloads and Technical Details',
    questions: [
      {
        question: 'How do I download my purchased digital photos?',
        answer: 'You\'ll receive an order confirmation email shortly after payment containing your download link. You can always access purchased images through your Orders section anytime.',
      },
      {
        question: 'How big are the digital images?',
        answer: 'Standard resolution photos are approximately 1 to 1.8MP, ideal for digital use and social media. High-res images are original size, typically between 4.5 and 10MP, suitable for high-quality printing.',
      },
      {
        question: 'Will my photos have a watermark?',
        answer: 'Purchased images have no SportPix watermark. Event-branded overlays on premium images are optional and fully chosen by you during purchase.',
      },
    ],

  },
  {
    topic: 'Account, Privacy, and Verification',
    questions: [
      {
        question: 'Why do you need my email and delivery address?',
        answer: 'Your email address receives your digital download links and invoice. Physical delivery address information is only required if you\'re ordering print photos.',
      },
      {
        question: 'How do I verify my account to access protected categories?',
        answer: 'Verification is done via your mobile phone number using a secure OTP (one-time pin), ensuring an extra layer of privacy and security.',
      },
      {
        question: 'Is my personal information safe?',
        answer: 'Yes. We respect your privacy and strictly adhere to data protection best practices. Your email or phone number are never shared or sold to third parties.',
      },
    ],

  },
  {
    topic: 'Sharing Photos & Rights',
    questions: [
      {
        question: 'Can I upload purchased photos to social media (Facebook, Instagram, etc)?',
        answer: 'Yes! Purchased digital photos are yours to freely share across your social networks.',
      },
      {
        question: 'Does buying a photo give me copyright ownership?',
        answer: 'No. You own your purchased image rights strictly for personal use, posting on social media, personal printing, or sharing with friends and family.',
      },
      {
        question: 'Can I use the photo commercially after buying it (e.g., for a business advertisement)?',
        answer: 'No. Commercial use of SportPix images (e.g., advertising, commercial websites, business posts or promotions) is strictly prohibited without prior explicit written permission from SportPix.',
      },
      {
        question: 'Can I resell or redistribute a purchased SportPix photo?',
        answer: 'No. All purchased images are for personal use only. Commercial resale or redistribution is prohibited and violates our terms of use.',
      },
    ],
  },
  {
    topic: 'Customer Support',
    questions: [
      {
        question: 'How can I reach out to SportPix for help or queries?',
        answer: 'Simply complete our Contact Us form on the website, or email us at support@sportpix.co.za, and we\'ll quickly respond to assist you.',
      },
      {
        question: 'Can I cancel my order or get a refund?',
        answer: 'Refunds are provided only if photos are objectively poor-quality (e.g., blurred). Digital photos thoroughly viewed and downloaded cannot be refunded. For print photos, refund or replacement applies to defective or incorrect prints.',
      },
    ],
  },
  {
    topic: 'Event Organiser Questions',
    questions: [
      {
        question: 'I\'m an event organiser. How can we arrange for SportPix at our event?',
        answer: 'Please get in touch via our dedicated events enquiry form or email us at support@sportpix.co.za. We\'ll contact you directly to discuss details, dates, and event needs.',
      },
      {
        question: 'Does it cost organisers money to bring SportPix to an event?',
        answer: 'Generally, no. Athlete photo sales directly cover SportPix\'s participation costs. However, for event sponsor-branded photos provided free to participants, commercial agreements apply and can be discussed with us directly.',
      },
    ],
  },
];

</script>
